/** This is a client-only component! Not SSR safe */
import React from 'react';
import { styled } from '@linaria/react';

import { BREAKPOINT_SIZES } from '@/constants';
import { clampedNormalize } from '@/utils';
import useWindowDimensions from '@/hooks/use-window-dimensions-client-only';
import useOnClickOutside from '@/hooks/use-on-click-outside';

import ColorPickerCompact from '@/components/ColorPickerCompact';
import Modal from '@/components/ModalBase';

import type { Color } from './GradientGenerator.helpers';
import FloatingBox from './FloatingBox';

interface Props {
  wrapperRef: React.RefObject<HTMLDivElement>;
  isVisible: boolean;
  color: Color;
  prefersReducedMotion: boolean;
  setColor: (newColor: Color) => void;
  onDismiss: () => void;
}

function ResponsiveColorPickerTooltip({
  wrapperRef,
  isVisible,
  color,
  prefersReducedMotion,
  setColor,
  onDismiss,
}: Props) {
  const windowDimensions = useWindowDimensions();

  if (!color) {
    return null;
  }

  const isMobile = windowDimensions.width <= BREAKPOINT_SIZES.md;

  return isMobile ? (
    <MobileColorPicker
      isVisible={isVisible}
      color={color}
      setColor={setColor}
      onDismiss={onDismiss}
    />
  ) : (
    <DesktopColorPicker
      wrapperRef={wrapperRef}
      isVisible={isVisible}
      color={color}
      setColor={setColor}
      onDismiss={onDismiss}
      windowDimensions={windowDimensions}
      prefersReducedMotion={prefersReducedMotion}
    />
  );
}

type MobileColorPickerProps = Omit<
  Props,
  'wrapperRef' | 'prefersReducedMotion'
>;

function MobileColorPicker({
  isVisible,
  color,
  setColor,
  onDismiss,
}: MobileColorPickerProps) {
  return (
    <Modal
      isOpen={isVisible}
      handleDismiss={onDismiss}
      dismissDuration={300}
    >
      <MobileColorPickerWrapper
        style={{ opacity: isVisible ? 1 : 0 }}
      >
        <ColorPickerCompact
          color={color}
          setColor={setColor}
          unitSize={270}
        />
      </MobileColorPickerWrapper>
    </Modal>
  );
}

type DesktopColorPickerProps = Props & {
  windowDimensions: { width: number; height: number };
};

function DesktopColorPicker({
  wrapperRef,
  isVisible,
  color,
  setColor,
  onDismiss,
  windowDimensions,
  prefersReducedMotion,
}: DesktopColorPickerProps) {
  // Dismiss popup when clicking elsewhere
  useOnClickOutside(wrapperRef, () => {
    if (isVisible) {
      onDismiss();
    }
  });

  // Dismiss popup when "Escape" is hit
  React.useEffect(() => {
    if (!isVisible) {
      return;
    }

    function handleKeydown(ev: KeyboardEvent) {
      if (ev.key === 'Escape') {
        onDismiss();
      }
    }
    window.addEventListener('keydown', handleKeydown);

    return () => {
      window.removeEventListener('keydown', handleKeydown);
    };

    // NOTE: I should add `onDismiss` to the dep array, but it isn't memoized and I'm too lazy to fix it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isVisible]);

  const unitSize = clampedNormalize(
    windowDimensions.height,
    400,
    800,
    150,
    275
  );

  return (
    <FloatingBox
      wrapperRef={wrapperRef}
      isVisible={isVisible}
      offset={2}
      prefersReducedMotion={prefersReducedMotion}
    >
      <ColorPickerCompact
        focusInputOnMount
        color={color}
        setColor={setColor}
        unitSize={unitSize}
      />
    </FloatingBox>
  );
}

const MobileColorPickerWrapper = styled.div`
  width: 100%;
  height: 100%;
  display: grid;
  place-content: center;
  transition: opacity 300ms;
`;

export default React.memo(ResponsiveColorPickerTooltip);
